/**
 * SEARCH MODAL MODULE
 * Full-screen luxury search modal for product search
 * Auto-initializes on page load
 */

class SearchModal {
  constructor() {
    this.isOpen = false;
    this.searchInput = null;
    this.results = [];
    this.init();
  }

  init() {
    this.createModal();
    this.attachEventListeners();
  }

  createModal() {
    const modalHTML = `
      <div class="search-modal" id="search-modal" aria-hidden="true" role="dialog" aria-labelledby="search-modal-title">
        <div class="search-modal-content">
          <!-- Close Button -->
          <button class="search-modal-close" id="search-modal-close" aria-label="Close search">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M8 8L24 24M24 8L8 24" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>

          <!-- Search Header -->
          <div class="search-modal-header">
            <h1 class="search-modal-title" id="search-modal-title">Search Products</h1>
          </div>

          <!-- Search Input -->
          <div class="search-modal-input-wrapper">
            <svg class="search-modal-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <input
              type="search"
              id="search-modal-input"
              class="search-modal-input"
              placeholder="Search for perfumes..."
              aria-label="Search products"
              autocomplete="off"
            >
          </div>

          <!-- Search Results -->
          <div class="search-modal-results" id="search-modal-results">
            <div class="search-modal-placeholder">
              <p>Start typing to search...</p>
            </div>
          </div>

          <!-- Recent Searches -->
          <div class="search-modal-recent" id="search-modal-recent">
            <div class="search-modal-section-title">Recent Searches</div>
            <div class="search-modal-recent-list" id="search-modal-recent-list"></div>
          </div>
        </div>
      </div>

      <div class="search-modal-backdrop" id="search-modal-backdrop"></div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Store references
    this.modal = document.getElementById('search-modal');
    this.backdrop = document.getElementById('search-modal-backdrop');
    this.closeBtn = document.getElementById('search-modal-close');
    this.searchInput = document.getElementById('search-modal-input');
    this.resultsContainer = document.getElementById('search-modal-results');
    this.recentContainer = document.getElementById('search-modal-recent-list');

    // Inject styles
    this.injectStyles();
  }

  injectStyles() {
    const styles = `
      .search-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        display: none;
        flex-direction: column;
        justify-content: flex-start;
        padding-top: 60px;
        overflow-y: auto;
      }

      .search-modal.active {
        display: flex;
      }

      .search-modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.8);
        z-index: -1;
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      .search-modal.active ~ .search-modal-backdrop {
        opacity: 1;
      }

      .search-modal-content {
        position: relative;
        z-index: 10000;
        max-width: 900px;
        margin: 0 auto;
        width: 100%;
        padding: 40px 20px;
      }

      .search-modal-close {
        position: absolute;
        top: 20px;
        right: 20px;
        background: none;
        border: none;
        color: var(--color-accent);
        cursor: pointer;
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        z-index: 10001;
      }

      .search-modal-close:hover {
        transform: rotate(90deg);
      }

      .search-modal-header {
        text-align: center;
        margin-bottom: 40px;
      }

      .search-modal-title {
        font-family: var(--font-heading);
        font-size: 48px;
        font-weight: 700;
        color: var(--color-text);
        margin: 0;
        letter-spacing: -1px;
      }

      .search-modal-input-wrapper {
        position: relative;
        margin-bottom: 40px;
      }

      .search-modal-input-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-accent);
        pointer-events: none;
      }

      .search-modal-input {
        width: 100%;
        background-color: rgba(255, 255, 255, 0.05);
        border: 2px solid var(--color-border-light);
        border-radius: 8px;
        padding: 16px 16px 16px 48px;
        font-family: var(--font-body);
        font-size: 16px;
        color: var(--color-text);
        transition: all 0.3s ease;
      }

      .search-modal-input:focus {
        outline: none;
        border-color: var(--color-accent);
        background-color: rgba(255, 255, 255, 0.08);
      }

      .search-modal-input::placeholder {
        color: var(--color-text-muted);
      }

      .search-modal-results {
        margin-bottom: 40px;
      }

      .search-modal-result-item {
        display: flex;
        gap: 16px;
        padding: 16px;
        border-bottom: 1px solid var(--color-border-light);
        text-decoration: none;
        color: inherit;
        transition: all 0.3s ease;
        align-items: center;
      }

      .search-modal-result-item:hover {
        background-color: rgba(212, 175, 55, 0.1);
        padding-left: 24px;
      }

      .search-modal-result-image {
        width: 80px;
        height: 80px;
        border-radius: 4px;
        object-fit: cover;
        background-color: var(--color-primary);
      }

      .search-modal-result-content {
        flex: 1;
      }

      .search-modal-result-title {
        font-weight: 600;
        color: var(--color-text);
        margin-bottom: 4px;
        font-size: 16px;
      }

      .search-modal-result-price {
        color: var(--color-accent);
        font-size: 14px;
      }

      .search-modal-placeholder {
        text-align: center;
        padding: 60px 20px;
        color: var(--color-text-muted);
      }

      .search-modal-recent {
        margin-top: 40px;
      }

      .search-modal-section-title {
        font-family: var(--font-heading);
        font-size: 18px;
        font-weight: 700;
        color: var(--color-text);
        margin-bottom: 16px;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      .search-modal-recent-list {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }

      .search-modal-recent-tag {
        background-color: rgba(255, 255, 255, 0.05);
        border: 1px solid var(--color-border-light);
        border-radius: 20px;
        padding: 8px 16px;
        color: var(--color-text);
        cursor: pointer;
        text-decoration: none;
        transition: all 0.3s ease;
        font-size: 14px;
      }

      .search-modal-recent-tag:hover {
        background-color: var(--color-accent);
        color: var(--color-primary);
        border-color: var(--color-accent);
      }

      @media (max-width: 767px) {
        .search-modal-content {
          padding: 20px;
        }

        .search-modal-title {
          font-size: 32px;
        }

        .search-modal-close {
          top: 10px;
          right: 10px;
        }
      }
    `;

    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }

  attachEventListeners() {
    // Close button
    this.closeBtn.addEventListener('click', () => this.close());

    // Backdrop click
    this.backdrop.addEventListener('click', () => this.close());

    // Search input
    this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    this.searchInput.addEventListener('focus', () => this.showRecent());

    // Keyboard
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        this.toggle();
      }
    });

    // Global search trigger
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-trigger]')) {
        e.preventDefault();
        this.open();
      }
    });
  }

  handleSearch(query) {
    if (!query.trim()) {
      this.showRecent();
      return;
    }

    // Fetch products (using Shopify search API)
    fetch(`/search?q=${encodeURIComponent(query)}&type=product&format=json`)
      .then(res => res.json())
      .then(data => {
        this.displayResults(data.results || []);
        this.saveRecent(query);
      })
      .catch(() => {
        this.resultsContainer.innerHTML = '<div class="search-modal-placeholder"><p>Error searching. Try again.</p></div>';
      });
  }

  displayResults(results) {
    if (results.length === 0) {
      this.resultsContainer.innerHTML = '<div class="search-modal-placeholder"><p>No products found.</p></div>';
      return;
    }

    const html = results.slice(0, 10).map(product => `
      <a href="${product.url}" class="search-modal-result-item">
        ${product.image ? `<img src="${product.image}" alt="${product.title}" class="search-modal-result-image">` : ''}
        <div class="search-modal-result-content">
          <div class="search-modal-result-title">${product.title}</div>
          ${product.price ? `<div class="search-modal-result-price">$${(product.price / 100).toFixed(2)}</div>` : ''}
        </div>
      </a>
    `).join('');

    this.resultsContainer.innerHTML = html;
  }

  showRecent() {
    const recent = JSON.parse(localStorage.getItem('searchHistory') || '[]');
    if (recent.length === 0) {
      this.resultsContainer.innerHTML = '<div class="search-modal-placeholder"><p>Your search history is empty</p></div>';
      return;
    }

    const html = recent.slice(0, 5).map(term => `
      <a href="/search?q=${encodeURIComponent(term)}" class="search-modal-recent-tag">${term}</a>
    `).join('');

    this.recentContainer.innerHTML = html;
  }

  saveRecent(query) {
    const recent = JSON.parse(localStorage.getItem('searchHistory') || '[]');
    const filtered = recent.filter(q => q.toLowerCase() !== query.toLowerCase());
    const updated = [query, ...filtered].slice(0, 10);
    localStorage.setItem('searchHistory', JSON.stringify(updated));
  }

  open() {
    this.isOpen = true;
    this.modal.classList.add('active');
    this.backdrop.classList.add('active');
    this.searchInput.focus();
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.isOpen = false;
    this.modal.classList.remove('active');
    this.backdrop.classList.remove('active');
    this.searchInput.value = '';
    document.body.style.overflow = '';
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  window.searchModal = new SearchModal();
});

// Export for manual use
window.SearchModal = SearchModal;
